import { Injectable } from '@angular/core';

/**
 * A single intercepted API call record.
 */
export interface ApiCallLogEntry {
  /** Absolute or relative request URL. */
  url: string;
  /** HTTP method (GET, POST, ...). */
  method: string;
  /** ISO timestamp captured when the request was fired. */
  timestamp: string;
  /** Request payload / input (parsed JSON when possible). */
  input: unknown;
  /** HTTP status code of the response (0 when the request failed at network level). */
  status?: number;
  /** Trace id extracted from the response headers, when present. */
  traceId?: string | null;
  /** Serialized error information when the request failed. */
  error?: unknown;
  /** Total round-trip time in milliseconds. */
  durationMs?: number;
  /** Underlying transport used for the call. */
  transport: 'fetch' | 'xhr';
}

/**
 * Persists intercepted API call metadata into sessionStorage.
 *
 * Entries are stored as a bounded, most-recent-last array so the log never
 * grows unbounded and blows the sessionStorage quota.
 */
@Injectable({ providedIn: 'root' })
export class ApiCallLoggerService {
  /** sessionStorage key under which the log array is stored. */
  static readonly STORAGE_KEY = 'apiCallLogs';

  /** Maximum number of entries kept in sessionStorage. */
  private readonly maxEntries = 300;

  log(entry: ApiCallLogEntry): void {
    try {
      const entries = this.getAll();
      entries.push(entry);

      // Keep only the most recent entries to stay within quota.
      const trimmed =
        entries.length > this.maxEntries
          ? entries.slice(entries.length - this.maxEntries)
          : entries;

      this.persist(trimmed);
    } catch {
      // If sessionStorage is full or unavailable, drop oldest half and retry once.
      try {
        const halved = this.getAll().slice(-Math.floor(this.maxEntries / 2));
        halved.push(entry);
        this.persist(halved);
      } catch {
        // Give up silently: logging must never break the actual API call.
      }
    }
  }

  getAll(): ApiCallLogEntry[] {
    try {
      const raw = sessionStorage.getItem(ApiCallLoggerService.STORAGE_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as ApiCallLogEntry[]) : [];
    } catch {
      return [];
    }
  }

  clear(): void {
    try {
      sessionStorage.removeItem(ApiCallLoggerService.STORAGE_KEY);
    } catch {
      // no-op
    }
  }

  private persist(entries: ApiCallLogEntry[]): void {
    sessionStorage.setItem(
      ApiCallLoggerService.STORAGE_KEY,
      JSON.stringify(entries),
    );
  }
}
